import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from '@emotion/styled'
import {
  EntityState,
  EntityType,
  EntityTypeEnum,
  FatFingerDetail,
  FatFingerProductSectorLimit,
  FatFingerUserLimit,
  ReferenceDataEntityType,
  WorkflowActions
} from 'api'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, NumericInputField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { Locale, Translation } from 'locale'
import { createReferenceDataEntityHook, useOnMount } from 'view/hooks'
import { ClonedComponentByShowModal, UnhappyModal } from 'view/shared/modal'
import { noop } from 'lodash'
import { fatFingerDetailsSelector } from 'selectors/fatFingerSelectors'
import { AllFatFingerSectorSelectboxField } from 'view/shared/entity/AllFingerOptions'
import { ValidationIssuesContainer } from 'view/shared/error'

interface FatFingerLimitDialogProps extends ClonedComponentByShowModal {
  organisationId: string
  existingFatFingerLimitIds?: string[]
  workflowId: string
  entityId: string
  userId: string
}

const Content = styled(Groupbox)`
  min-width: 400px;
  min-height: 250px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  padding: 0 20px;

  & > * {
    margin-bottom: 19px;
  }
`

export const GridWrapper = styled.div<{ height: number }>`
  height: ${props => props.height}px;
  width: 100%;
`

const getDefaultFatFingerSector = (defaultFatFingers: FatFingerDetail[], id: string) => {
  const fatFingerFound = defaultFatFingers.map(element => element.fatFinger).find(x => x.productSectorId === id)

  if (fatFingerFound) {
    return fatFingerFound
  } else {
    return defaultFatFingers.map(element => element.fatFinger).find(x => x.productSectorId === 'DEFAULT_ID')
  }
}
const useReferenceDataFatFinger = createReferenceDataEntityHook(EntityType.FAT_FINGER_USER)

export const FatFingerLimitDialog = (props: FatFingerLimitDialogProps) => {
  const fatFingerLimitDetail = useReferenceDataFatFinger({ workflowId: props.workflowId, entityId: props.entityId })

  if (!fatFingerLimitDetail) {
    return <UnhappyModal title="Fat Finger Limit not found." />
  }

  return <FatFingerLimitDialogComponent {...props} fatFingerLimitDetail={fatFingerLimitDetail} />
}

const FatFingerLimitDialogComponent: React.FC<FatFingerLimitDialogProps & {
  fatFingerLimitDetail: ReferenceDataEntityType<FatFingerUserLimit>
}> = ({ workflowId, entityId, onClose, fatFingerLimitDetail, existingFatFingerLimitIds, userId }) => {
  const dispatch = useDispatch()

  const isNew = fatFingerLimitDetail.entityState === EntityState.NEW

  const localFatFingerLimitDetail: FatFingerUserLimit = useMemo(() => fatFingerLimitDetail?.data, [fatFingerLimitDetail?.data])

  const initialFatFingerLimit = useRef(localFatFingerLimitDetail)

  const title = isNew ? Locale.translate(Translation.TC_NEW_FAT_FINGER_LIMIT) : Locale.translate(Translation.TC_EDIT_FAT_FINGER_LIMIT)

  const defaultFatFingers = useSelector(fatFingerDetailsSelector)

  const [currentFatFingerSector, setCurrentFatFingerSector] = useState<FatFingerProductSectorLimit | undefined>(() => {
    return localFatFingerLimitDetail.productSectorId !== null
      ? getDefaultFatFingerSector(defaultFatFingers, localFatFingerLimitDetail.productSectorId)
      : undefined
  })

  const handleUpdate = useCallback(
    (update: Partial<FatFingerUserLimit>) => {
      dispatch(
        WorkflowActions.createEntityUpdateReferenceData<FatFingerUserLimit>({
          entityId: localFatFingerLimitDetail.id,
          entityType: EntityTypeEnum.FAT_FINGER_USER,
          updatedEntity: {
            ...localFatFingerLimitDetail,
            ...update,
            userId
          },
          workflowId
        })
      )
    },
    [localFatFingerLimitDetail, dispatch, workflowId]
  )

  const handleCancel = () => {
    if (fatFingerLimitDetail.entityState === EntityState.NEW) {
      dispatch(WorkflowActions.createEntityDeleteReferenceData([{ entityId, entityType: EntityTypeEnum.FAT_FINGER_USER }], workflowId))
    } else {
      dispatch(
        WorkflowActions.createEntityUpdateReferenceData({
          entityId: initialFatFingerLimit.current.id,
          entityType: EntityTypeEnum.FAT_FINGER_USER,
          updatedEntity: initialFatFingerLimit.current,
          workflowId
        })
      )
    }

    if (onClose) {
      onClose()
    }
  }

  const handleSave = () => {
    if (onClose) {
      onClose()
    }
  }

  useOnMount(() => {
    handleUpdate(localFatFingerLimitDetail)
  })

  const handleProductSectorChanged = (update: any) => {
    const current = getDefaultFatFingerSector(defaultFatFingers, update)
    setCurrentFatFingerSector(current)
    handleUpdate({ ...current, productSectorId: update })
  }

  return (
    <ModalWindow title={title}>
      <Content>
        {currentFatFingerSector?.productSectorId === 'DEFAULT_ID' && localFatFingerLimitDetail.productSectorId !== '' && (
          <ValidationIssuesContainer>{Locale.translate(Translation.SC_NO_FAT_FINGER_SECTOR)}</ValidationIssuesContainer>
        )}
        <DialogRow>
          <DialogField>
            <AllFatFingerSectorSelectboxField
              dataTestId={'FatFingerLimitDialog-FatfingerSectorSelectboxField-Sector'}
              label={Locale.translate(Translation.SC_SECTOR)}
              onUpdate={handleProductSectorChanged}
              value={localFatFingerLimitDetail.productSectorId}
              validationField={'productSectorId'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
              excludeOptionKeys={existingFatFingerLimitIds}
              disabled={!isNew}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <NumericInputField
              label={Locale.translate(Translation.SC_FIXED_RATE_PRICE)}
              readOnly={true}
              onUpdate={noop}
              value={currentFatFingerSector?.fixedRatePrice}
              formatOptions={{ minDp: 3, percent: true, decimalShift: 2 }}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              label={Locale.translate(Translation.SC_FLOAT_RATE_SPREAD)}
              readOnly={true}
              onUpdate={noop}
              value={currentFatFingerSector?.floatRateSpread}
              formatOptions={{ decimalShift: 4, separator: false }}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <NumericInputField
              dataTestId={'FatFingerLimitDialog-NumericInputField-FixedRateWarnLevel'}
              label={Locale.translate(Translation.SC_FIXED_RATE_WARN_LEVEL)}
              onUpdate={value => handleUpdate({ fixedRateWarnLevel: value })}
              value={localFatFingerLimitDetail.fixedRateWarnLevel}
              validationField={'fixedRateWarnLevel'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              dataTestId={'FatFingerLimitDialog-NumericInputField-FloatRateWarnLevel'}
              label={Locale.translate(Translation.SC_FLOAT_RATE_WARN_LEVEL)}
              onUpdate={value => handleUpdate({ floatRateWarnLevel: value })}
              value={localFatFingerLimitDetail.floatRateWarnLevel}
              validationField={'floatRateWarnLevel'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <NumericInputField
              dataTestId={'FatFingerLimitDialog-NumericInputField-FixedRateRestriction'}
              label={Locale.translate(Translation.SC_FIXED_RATE_RESTRICTION)}
              onUpdate={value => handleUpdate({ fixedRateRestriction: value })}
              value={localFatFingerLimitDetail.fixedRateRestriction}
              validationField={'fixedRateRestriction'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              dataTestId={'FatFingerLimitDialog-NumericInputField-FloatRateRestriction'}
              label={Locale.translate(Translation.SC_FLOAT_RATE_RESTRICTION)}
              onUpdate={value => handleUpdate({ floatRateRestriction: value })}
              value={localFatFingerLimitDetail.floatRateRestriction}
              validationField={'floatRateRestriction'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <NumericInputField
              dataTestId={'FatFingerLimitDialog-NumericInputField-NotionalAmountWarn'}
              label={Locale.translate(Translation.SC_NOTIONAL_AMOUNT_WARN)}
              onUpdate={value => handleUpdate({ notionalAmountWarn: value })}
              value={localFatFingerLimitDetail.notionalAmountWarn}
              formatOptions={{ decimalShift: -6, separator: true }}
              validationField={'notionalAmountWarn'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
              maxLength={13}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              dataTestId={'FatFingerLimitDialog-NumericInputField-NotionalAmountRestriction'}
              label={Locale.translate(Translation.SC_NOTIONAL_AMOUNT_RESTRICTION)}
              onUpdate={value => handleUpdate({ notionalAmountRestriction: value })}
              value={localFatFingerLimitDetail.notionalAmountRestriction}
              formatOptions={{ decimalShift: -6, separator: true }}
              validationField={'notionalAmountRestriction'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
              maxLength={13}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <NumericInputField
              dataTestId={'FatFingerLimitDialog-NumericInputField-CashAmountWarn'}
              label={Locale.translate(Translation.SC_CASH_AMOUNT_WARN)}
              onUpdate={value => handleUpdate({ cashAmountWarn: value })}
              value={localFatFingerLimitDetail.cashAmountWarn}
              formatOptions={{ decimalShift: -6, separator: true }}
              validationField={'cashAmountWarn'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
            />
          </DialogField>
          <DialogField>
            <NumericInputField
              dataTestId={'FatFingerLimitDialog-NumericInputField-CashAmountRestriction'}
              label={Locale.translate(Translation.SC_CASH_AMOUNT_RESTRICTION)}
              onUpdate={value => handleUpdate({ cashAmountRestriction: value })}
              value={localFatFingerLimitDetail.cashAmountRestriction}
              formatOptions={{ decimalShift: -6, separator: true }}
              validationField={'cashAmountRestriction'}
              validationIssues={fatFingerLimitDetail.validationIssues ?? []}
            />
          </DialogField>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'FatFingerLimitDialog-TextButton-ButtonCancel'} type={TextButtonType.Bad} onClick={handleCancel}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'FatFingerLimitDialog-TextButton-ButtonSave'}
              type={TextButtonType.Good}
              disabled={!(fatFingerLimitDetail.validationIssues?.length === 0)}
              onClick={handleSave}>
              {Locale.translate(Translation.TC_BUTTON_OK)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
